/**
 * Payment Complete Page
 * 
 * Handles the redirect from Stripe after payment
 * Verifies payment status and finalizes the reservation
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';

import Loader from '../Loader';
import Link from '../Link';
import { payment } from '../../config';
import messages from '../../locale/messages';

import s from './PaymentComplete.css';

// Load Stripe
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(payment.stripe.publishableKey);
  }
  return stripePromise;
};

class PaymentComplete extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      status: 'loading',
      paymentIntentId: null,
      errorMessage: null,
      reservationId: null,
    };
  }

  async componentDidMount() {
    await this.checkPaymentStatus();
  }

  checkPaymentStatus = async () => {
    try {
      const stripe = await getStripe();
      
      // Get the payment_intent_client_secret from URL
      const urlParams = new URLSearchParams(window.location.search);
      const clientSecret = urlParams.get('payment_intent_client_secret');
      const paymentIntentId = urlParams.get('payment_intent');
      const listId = urlParams.get('listId');

      if (!clientSecret) {
        this.setState({
          status: 'error',
          errorMessage: 'Missing payment information. Please try again.',
        });
        return;
      }

      // Retrieve the PaymentIntent
      const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);

      if (error) {
        this.setState({
          status: 'error',
          errorMessage: error.message,
        });
        return;
      }

      this.setState({ paymentIntentId: paymentIntent.id });

      // Check the payment status
      switch (paymentIntent.status) {
        case 'succeeded':
          // Payment successful - finalize reservation on server
          await this.finalizeReservation(paymentIntent.id, listId);
          break;
        
        case 'processing':
          this.setState({
            status: 'processing',
          });
          break;
        
        case 'requires_payment_method':
          this.setState({
            status: 'failed',
            errorMessage: 'Payment failed. Please try another payment method.',
          });
          break;
        
        default:
          this.setState({
            status: 'error',
            errorMessage: `Unexpected payment status: ${paymentIntent.status}`,
          });
      }
    } catch (error) {
      console.error('[PaymentComplete] Error:', error);
      this.setState({
        status: 'error',
        errorMessage: error.message || 'An unexpected error occurred.',
      });
    }
  };

  finalizeReservation = async (paymentIntentId, listId) => {
    try {
      // Get reservation data from sessionStorage
      let reservationData = null;
      if (typeof window !== 'undefined') {
        const storedData = sessionStorage.getItem('stripeReservationData');
        if (storedData) {
          reservationData = JSON.parse(storedData);
          console.log('[PaymentComplete] Retrieved reservation data from sessionStorage');
          // Clear after reading
          sessionStorage.removeItem('stripeReservationData');
        }
      }

      if (!reservationData) {
        console.error('[PaymentComplete] No reservation data found in sessionStorage');
        this.setState({
          status: 'error',
          errorMessage: 'Reservation data not found. Please contact support.',
        });
        return;
      }

      const response = await fetch('/stripe-elements-confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          reservationData,
        }),
        credentials: 'include',
      });

      const data = await response.json();

      if (data.status === 200) {
        this.setState({
          status: 'succeeded',
          reservationId: data.reservationId,
        });
        
        // Redirect to itinerary after a short delay
        if (data.redirect) {
          setTimeout(() => {
            window.location.href = data.redirect;
          }, 2000);
        }
      } else {
        // Payment succeeded but reservation failed - still show success
        // The payment went through, we just couldn't finalize on our end
        this.setState({
          status: 'succeeded',
          errorMessage: data.error,
        });
      }
    } catch (error) {
      console.error('[PaymentComplete] Error finalizing reservation:', error);
      // Payment succeeded but we couldn't communicate with server
      this.setState({
        status: 'succeeded',
        errorMessage: 'Payment successful, but we had trouble updating your reservation. Please contact support.',
      });
    }
  };

  renderStatus() {
    const { status, errorMessage, paymentIntentId } = this.state;
    const { formatMessage } = this.props.intl;

    switch (status) {
      case 'loading':
        return (
          <div className={s.statusContainer}>
            <div className={s.loaderWrapper}>
              <Loader type="text" />
            </div>
            <h2 className={s.statusTitle}>
              <FormattedMessage {...messages.processingPayment} />
            </h2>
            <p className={s.statusText}>
              <FormattedMessage {...messages.pleaseWait} />
            </p>
          </div>
        );

      case 'succeeded':
        return (
          <div className={s.statusContainer}>
            <div className={cx(s.statusIcon, s.successIcon)}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M14 24L21 31L34 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className={s.statusTitle}>
              <FormattedMessage {...messages.paymentSuccessful} />
            </h2>
            <p className={s.statusText}>
              <FormattedMessage {...messages.thankYouForBooking} />
            </p>
            {errorMessage && (
              <p className={s.warningText}>{errorMessage}</p>
            )}
            <div className={s.redirectInfo}>
              <Loader type="text" />
              <span><FormattedMessage {...messages.redirecting} /></span>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className={s.statusContainer}>
            <div className={cx(s.statusIcon, s.processingIcon)}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#6B7280"/>
                <circle cx="24" cy="24" r="12" stroke="white" strokeWidth="3" fill="none" strokeDasharray="60" strokeDashoffset="15">
                  <animateTransform attributeName="transform" type="rotate" from="0 24 24" to="360 24 24" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <h2 className={s.statusTitle}>
              <FormattedMessage {...messages.paymentProcessing} />
            </h2>
            <p className={s.statusText}>
              <FormattedMessage {...messages.paymentProcessingText} />
            </p>
          </div>
        );

      case 'failed':
      case 'error':
        return (
          <div className={s.statusContainer}>
            <div className={cx(s.statusIcon, s.errorIcon)}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#EF4444"/>
                <path d="M16 16L32 32M32 16L16 32" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className={s.statusTitle}>
              <FormattedMessage {...messages.paymentFailed} />
            </h2>
            <p className={s.statusText}>
              {errorMessage || <FormattedMessage {...messages.paymentFailedText} />}
            </p>
            <Link to="/s" className={s.retryButton}>
              <FormattedMessage {...messages.tryAgainButton} />
            </Link>
          </div>
        );

      default:
        return null;
    }
  }

  render() {
    return (
      <div className={s.container}>
        <div className={s.card}>
          {this.renderStatus()}
        </div>
      </div>
    );
  }
}

const mapState = (state) => ({});

export default injectIntl(
  withStyles(s)(
    connect(mapState)(PaymentComplete)
  )
);
